import { useState, useCallback, useRef, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import type { MessageData } from '../components/MessageList';
import { ConversationSummary } from '../components/ConversationList';
import api, { ConversationDetailResponse } from '../services/api';
import { resetProjectListSnapshot } from '../utils/projectListSnapshot';
import { nowCSTISO } from '../utils/datetime';
import { useTranslation } from '../contexts/LanguageContext';
import { provisionalEchoId } from '../utils/provisionalEchoId';

/**
 * Personal relay: epoch ms at which a message expires, or null when the
 * timestamp is unparseable (treated as expired by `isRelayMessageExpired`,
 * mirroring `personal_relay.py::is_relay_expired` None → expired).
 */
export function relayExpiresAtMs(createdAt: string, ttlSeconds: number): number | null {
  const ts = new Date(createdAt).getTime();
  return Number.isNaN(ts) ? null : ts + ttlSeconds * 1000;
}

/** Personal relay: true once `created_at + ttl` has passed (or is unknown). */
export function isRelayMessageExpired(createdAt: string, ttlSeconds: number, nowMs: number = Date.now()): boolean {
  const exp = relayExpiresAtMs(createdAt, ttlSeconds);
  return exp === null || exp <= nowMs;
}

/** Translator signature injected into module-level error mappers. */
type TFunction = (key: string, params?: Record<string, string | number>) => string;

/** Max retries for retryWithBackoff. */
const RETRY_MAX_ATTEMPTS = 3;

/** Exponential backoff delays (ms) for each retry attempt. */
const RETRY_DELAYS = [1000, 2000, 4000];

/** Max conversations held in the tab-lifetime detail cache (LRU eviction). */
const DETAIL_CACHE_MAX_ENTRIES = 10;

/**
 * True when the rejection is a cancel the app itself requested via
 * AbortController — axios raises CanceledError (``__CANCEL__``), the native
 * AbortSignal path raises AbortError. A cancel is an intentional supersede,
 * never a failure: it must not retry, surface an error, or log as an error.
 */
function isCancelledError(err: unknown): boolean {
  if (axios.isCancel(err)) return true;
  const name = (err as { name?: string } | null | undefined)?.name;
  return name === 'CanceledError' || name === 'AbortError';
}

/**
 * Determines whether an Axios error is retryable.
 * Retries on: network errors, timeouts, and 5xx server errors.
 * Does NOT retry on 4xx (especially 401, 403, 404) -- these are permanent failures.
 */
function isRetryableError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const axErr = err as AxiosError;
  // Network error (no response received)
  if (axErr.message === 'Network Error') return true;
  // Timeout
  if (axErr.code === 'ECONNABORTED') return true;
  // 5xx server errors
  const status = axErr.response?.status;
  if (status !== undefined && status >= 500) return true;
  return false;
}

/**
 * Retry an async operation with exponential backoff.
 * Only retries on retryable errors (network, timeout, 5xx).
 * Throws the last error if all attempts are exhausted.
 */
async function retryWithBackoff<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      // Rethrow cancels unchanged so every caller's catch can recognise and
      // discard them; retrying a request the app just aborted defeats the abort.
      if (isCancelledError(err)) throw err;
      if (attempt < RETRY_MAX_ATTEMPTS && isRetryableError(err)) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
        continue;
      }
      throw err;
    }
  }
  // Unreachable, but satisfies TypeScript
  throw lastError;
}

/**
 * Returns a user-facing, localized error message based on the error type.
 * The ``t`` translator is injected by the hook (module-level functions cannot
 * call ``useTranslation`` directly).
 */
function getErrorMessage(err: unknown, t: TFunction): string {
  if (!(err instanceof Error)) return t('errors.conversation.load_failed');
  const axErr = err as AxiosError;
  // Timeout
  if (axErr.code === 'ECONNABORTED') return t('errors.conversation.timeout');
  // Network error
  if (axErr.message === 'Network Error') return t('errors.conversation.network');
  const status = axErr.response?.status;
  if (status !== undefined) {
    if (status >= 500) return t('errors.conversation.server');
    if (status === 403) return t('errors.conversation.forbidden');
    if (status === 404) return t('errors.conversation.not_found');
  }
  return t('errors.conversation.load_failed');
}

/**
 * Full conversation data including messages.
 * Maps from backend ConversationDetail schema.
 */
export interface Conversation {
  /** Unique conversation identifier */
  id: string;
  /** Associated project ID */
  project_id: string;
  /** Project display name */
  project_name: string;
  /** Whether conversation is active */
  is_active: boolean;
  /** Whether this is a group conversation */
  is_group?: boolean;
  /** Compacted conversation summary */
  compact_summary: string | null;
  /**
   * Auto-generated conversation title (`orchestrator.py::_generate_theme_async`).
   * NULL is the NORMAL state for a brand-new conversation — generation is
   * asynchronous — so every consumer must keep a fallback (chat header falls
   * back to `project_name`). The backend has always emitted this field; the
   * mapper used to discard it silently.
   */
  theme?: string | null;
  /** All messages in the conversation */
  messages: MessageData[];
  /** Pending confirmation action */
  pending_action: Record<string, unknown> | null;
  /** ISO 8601 creation timestamp */
  created_at: string;
  /** ISO 8601 last update timestamp */
  updated_at: string;
  /** Method A provider profile name; null => backend default ("anthropic"). */
  provider_profile?: string | null;
  /** Models available for this conversation's provider (drives InputArea selector). */
  allowed_models?: string[];
  /** Per-conversation multimodel routing fields (Per-Project Backend Plan Sub-Task 1/3). */
  architecture_mode?: 'claude_cli' | 'conductor_native' | 'codex_cli' | null;
  // 'native_loop' is a legal backend_kind (dispatcher.py::resolve_backend, and
  // the create/update validators accept {claude, aux_only, native_loop}); it was
  // missing from this union, so every native_loop conversation was a type lie.
  backend_kind?: 'claude' | 'aux_only' | 'native_loop' | null;
  backend_model?: string | null;
  /** Workspace mode + dev sub-mode for the chat-header mode badge
   *  (P-COND-403CF74F433B). null = legacy conversation with no explicit mode. */
  mode?: 'dev' | 'collab' | null;
  dev_submode?: 'branch_dev' | 'operation' | null;
  collab_submode?: 'planning' | 'design' | 'production' | null;
  /** Personal relay (个人中转站): owner-only, never dispatched. */
  is_personal_relay?: boolean;
  /** Per-message lifetime (s) for a relay conversation; null otherwise. */
  relay_ttl_seconds?: number | null;
  /** Server-owned purpose; observation is read-only execution, not disabled chat. */
  purpose?: string | null;
  execution_read_only?: boolean;
  branched_from_conversation_id?: string | null;
  /** Review-child lineage (P-COND-DEF54F26332F ST3): drives 「← 返回原会话」. */
  parent_conversation_id?: string | null;
  conversation_kind?: string | null;
}

/** Page size for conversation list pagination (LIMIT+1 pattern: request PAGE_SIZE+1). */
const CONV_PAGE_SIZE = 50;

/** Debounce interval (ms) for sidebar conversation list silent refresh. */
const SIDEBAR_REFRESH_DEBOUNCE_MS = 5000;
/** Visible-tab fallback for missed sidebar notifications. */
export const SIDEBAR_RECONCILE_INTERVAL_MS = 30000;

/** Debounce interval (ms) for single-conversation message silent refresh. */
const CONV_REFRESH_DEBOUNCE_MS = 5000;

/**
 * Return type for the useConversation hook.
 */
interface UseConversationReturn {
  /** List of all conversation summaries */
  conversations: ConversationSummary[];
  /** The currently active conversation (full data with messages) */
  activeConversation: Conversation | null;
  /** Loading and errors belong to the resource that initiated the request. */
  isListLoading: boolean;
  isDetailLoading: boolean;
  listError: string | null;
  detailError: string | null;
  createError: string | null;
  /** Whether more historical messages are available */
  hasMore: boolean;
  /** Whether older messages are currently being loaded */
  isLoadingMore: boolean;
  /** Whether more conversations are available (list pagination) */
  hasMoreConversations: boolean;
  /** Whether more conversations are currently being loaded */
  isLoadingMoreConversations: boolean;
  /** Loads the list of conversation summaries */
  loadConversations: () => Promise<void>;
  /**
   * Loads a specific conversation with its messages.
   * ``silent`` suppresses the spinner/error visual transitions (used by the
   * cache-hit background revalidate) while keeping the stale-fetch guards.
   */
  loadConversation: (conversationId: string, options?: { silent?: boolean }) => Promise<void>;
  /** Loads older messages (pagination) */
  loadMoreMessages: () => Promise<void>;
  /** Loads the next page of conversations */
  loadMoreConversations: () => Promise<void>;
  /** Creates a new conversation (requires project_id) */
  createConversation: (projectId: string, initialMessage?: string) => Promise<string>;
  /** Adds a message to the active conversation (optimistic update) */
  addMessage: (message: MessageData, expectedConvId: string) => void;
  /** Updates a message in the active conversation (for streaming) */
  updateMessage: (messageId: string, content: string, expectedConvId: string, status?: MessageData['status']) => void;
  /**
   * Reconciles an optimistic temp user bubble with the authoritative server echo.
   * ``adoptServerId`` is ONLY for frames flagged ``persisted_row: true`` (their id
   * IS the DB id); every other echo keeps the provisional temp id.
   */
  reconcileOptimisticUserMessage: (
    serverMsg: MessageData,
    expectedConvId: string,
    opts?: { adoptServerId?: boolean },
  ) => void;
  /** Drop messages by id from the active conversation (relay delete / expiry). */
  removeMessages: (ids: string[], convId: string) => void;
  /** Sets the active conversation by ID */
  setActiveConversationId: (conversationId: string | null) => void;
  /** Silently refresh conversation list without showing loading spinner */
  silentRefreshConversations: (force?: boolean) => Promise<void>;
  /**
   * True when this mount started from the tab-lifetime sidebar snapshot (a
   * ChatPage remount): the caller should revalidate silently, not with a spinner.
   */
  hasWarmConversationList: boolean;
  /** Silently refresh active conversation messages without loading spinner.
   *  `force` skips the 5s debounce and is queued (not dropped) behind an
   *  in-flight refresh. */
  silentRefreshConversation: (conversationId: string, opts?: { force?: boolean }) => Promise<void>;
  /** Patch the active conversation's collab sub-mode in place */
  updateConversationSubmode: (
    conversationId: string,
    submode: 'planning' | 'design' | 'production' | null,
  ) => void;
  /**
   * Optimistically update provider_profile on the active conversation, and —
   * when the server echoes them back — the routing fields that move WITH the
   * provider. PATCH /provider_profile resolves and persists the triple
   * (provider_profile, architecture_mode, backend_kind) server-side; dropping
   * the latter two left the UI believing the old architecture until the next
   * full fetch.
   */
  updateConversationProviderProfile: (
    conversationId: string,
    profile: string | null,
    routing?: {
      architecture_mode?: Conversation['architecture_mode'];
      backend_kind?: Conversation['backend_kind'];
    },
  ) => void;
  /**
   * Apply the routing axis the orchestrator reports at dispatch start (ws
   * `task_started`). Writes the SAME `activeConversation.architecture_mode`
   * field the render chain already reads — no parallel state. This is the only
   * path that reflects a per-message model switch, which never PATCHes the row.
   */
  updateConversationArchitectureMode: (
    conversationId: string,
    architectureMode: Conversation['architecture_mode'],
  ) => void;
  /** Optimistically update dev_submode on the active conversation */
  updateConversationDevSubmode: (
    conversationId: string,
    devSubmode: 'branch_dev' | 'operation' | null,
  ) => void;
  /** Optimistically remove a conversation from the list (e.g. after archiving) */
  removeConversation: (conversationId: string) => void;
  /** Optimistically mark a conversation as read in the local list */
  markConversationRead: (conversationId: string) => void;
  /**
   * Drop one conversation's entry from the page-lifetime detail cache, so the
   * next visit loads fresh (spinner) instead of rendering a stale snapshot.
   * For conversations that changed while NOT on screen; unchanged ones keep
   * their instant cache hit (#757).
   */
  invalidateConversationCache: (id: string) => void;
  /** Whether the server-side "favorites only" filter is active */
  favoriteFilter: boolean;
  /** Toggle the server-side favorites-only filter (reloads the list) */
  setFavoriteFilter: (next: boolean) => void;
  /** Set the server-side search query (会话ID/分支/标题/内容); reloads the list */
  setSearchFilter: (next: string) => void;
  /** Set the org scope for the conversation list (null = cross-org); reloads */
  setOrgScope: (orgId: string | null) => void;
  /** Set the project scope for the conversation list (null = all projects); reloads */
  setProjectScope: (projectId: string | null) => void;
  /** Set active/archived scope for the conversation list (true = active); reloads */
  setActiveFilter: (next: boolean) => void;
  /** Optimistically flip a conversation's favorite state + persist via API */
  toggleConversationFavorite: (conversationId: string, next: boolean) => void;
  /** Clear the current error */
  clearError: (resource: 'list' | 'detail' | 'create') => void;
  /** Retry the last failed load (conversations list or active conversation) */
  retryLoad: (resource: 'list' | 'detail') => void;
}

/**
 * One page-lifetime cache entry. The pagination markers travel WITH the
 * conversation: restoring messages without them would leave "load more"
 * pointing at the previously active conversation's cursor.
 */
interface CachedConversationDetail {
  conversation: Conversation;
  hasMore: boolean;
  oldestMessageId: string | null;
}

// Tab-lifetime (memory only — no storage, no serialization) LRU cache of
// loaded conversation details, keyed by conversation id. Module-level so it
// survives a ChatPage remount (e.g. Skill Center -> back to the conversation);
// a re-visit renders from it instantly and revalidates in the background
// (stale-while-revalidate). Cleared on logout via resetConversationDetailCache.
const conversationDetailCache = new Map<string, CachedConversationDetail>();

/**
 * Tab-lifetime snapshot of the sidebar's conversation list (memory only), so a
 * ChatPage remount (Skill Center / profile / org pages -> back to chat) renders
 * the last list instantly instead of the 「加载中」 placeholder, then revalidates
 * silently. The scope the list was fetched under travels WITH it: the hook seeds
 * its scope refs from here, so the mount-time setOrgScope / setProjectScope /
 * setActiveFilter calls no-op when the scope still matches and do a normal full
 * load when it does not (bug-patterns #957: instance-lifetime caches).
 */
interface ConversationListSnapshot {
  conversations: ConversationSummary[];
  hasMore: boolean;
  offset: number;
  orgScope: string | null;
  projectScope: string | null;
  favorite: boolean;
  active: boolean;
}
let conversationListSnapshot: ConversationListSnapshot | null = null;

/**
 * Logout / test hook: forget every cached conversation detail plus the sidebar
 * list and project snapshots (all per-user — never reuse across users).
 */
export function resetConversationDetailCache(): void {
  conversationDetailCache.clear();
  conversationListSnapshot = null;
  resetProjectListSnapshot(); // utils/projectListSnapshot.ts
}

/**
 * Maps backend ConversationDetail response to local Conversation type.
 */
function mapConversationDetail(detail: ConversationDetailResponse): Conversation {
  return {
    id: detail.id,
    project_id: detail.project_id,
    project_name: detail.project_name,
    is_active: detail.is_active,
    is_group: detail.is_group,
    compact_summary: detail.compact_summary,
    theme: detail.theme ?? null,
    messages: sortByCreatedAt(detail.messages.map((msg) => ({
      id: msg.id,
      role: msg.role as 'user' | 'assistant' | 'system',
      source: msg.source,
      content: msg.content,
      created_at: msg.created_at,
      content_rich: msg.content_rich,
      sender_name: msg.sender_name,
      metadata: msg.metadata,
      attachments: msg.attachments,
    }))),
    pending_action: detail.pending_action,
    created_at: detail.created_at,
    updated_at: detail.updated_at,
    provider_profile: detail.provider_profile ?? null,
    allowed_models: detail.allowed_models ?? [],
    architecture_mode: detail.architecture_mode ?? null,
    backend_kind: detail.backend_kind ?? null,
    backend_model: detail.backend_model ?? null,
    mode: detail.mode ?? null,
    dev_submode: detail.dev_submode ?? null,
    collab_submode: detail.collab_submode ?? null,
    is_personal_relay: detail.is_personal_relay ?? false,
    relay_ttl_seconds: detail.relay_ttl_seconds ?? null,
    purpose: detail.purpose ?? null,
    execution_read_only: detail.execution_read_only,
    branched_from_conversation_id: detail.branched_from_conversation_id ?? null,
    parent_conversation_id: detail.parent_conversation_id ?? null,
    conversation_kind: detail.conversation_kind ?? null,
  };
}

/**
 * Sort messages by created_at timestamp (ascending).
 * Pre-computes numeric timestamps to avoid repeated Date construction
 * in the comparator (O(n) Date parses instead of O(n log n)).
 * Applied after every merge to guarantee chronological order.
 */
function sortByCreatedAt(msgs: MessageData[]): MessageData[] {
  const withTs = msgs.map(m => ({ m, ts: new Date(m.created_at).getTime() }));
  withTs.sort((a, b) => a.ts - b.ts);
  return withTs.map(t => t.m);
}

/**
 * Custom hook for conversation state management.
 * Manages the list of conversations, active conversation,
 * messages, and provides CRUD operations via the API service.
 */
const useConversation = (): UseConversationReturn => {
  const { t } = useTranslation();
  // Mirror the translator into a ref so the stable (empty-dep) fetch callbacks
  // read the current ``t`` without a stale closure — same pattern as the
  // org/project scope refs below.
  const tRef = useRef(t);
  tRef.current = t;

  // Captured once per mount: the sidebar snapshot left by a previous ChatPage.
  const [initialListSnapshot] = useState(() => conversationListSnapshot);
  const [conversations, setConversations] = useState<ConversationSummary[]>(
    () => initialListSnapshot?.conversations ?? [],
  );
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  // Mirror for identity checks inside the stable (empty-dep) callbacks. Putting
  // activeConversation in a dep array instead would re-create the callback on
  // every streamed message and churn every consumer.
  const activeConversationRef = useRef<Conversation | null>(null);
  activeConversationRef.current = activeConversation;
  const [isListLoading, setIsListLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  // Data validity and spinner ownership differ: silent refresh only owns data.
  const listLoadingOwnerRef = useRef(0);
  const detailReqSeqRef = useRef(0);
  const failedDetailIdRef = useRef<string | null>(null);

  // Message pagination state
  const [hasMore, setHasMore] = useState(false);
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Conversation list pagination state
  const [hasMoreConversations, setHasMoreConversations] = useState(
    () => initialListSnapshot?.hasMore ?? false,
  );
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState(false);
  const conversationsOffsetRef = useRef<number>(initialListSnapshot?.offset ?? 0);
  // Only a list that a fetch actually produced may become the snapshot — never
  // the empty initial state of a mount that is left before its first fetch.
  const listFetchedRef = useRef<boolean>(initialListSnapshot !== null);

  // Server-side "favorites only" filter. The ref mirrors the state so the
  // fetch callbacks (which have empty dep arrays to stay stable) read the
  // current value without a stale closure. When ON, all three list fetches
  // pass favorite=true so favorited convs past the pagination window surface.
  const [favoriteFilter, setFavoriteFilterState] = useState(
    () => initialListSnapshot?.favorite ?? false,
  );
  const favoriteFilterRef = useRef<boolean>(initialListSnapshot?.favorite ?? false);

  // Server-side search query (会话ID/分支/标题/内容). The ref mirrors the
  // debounced search term so the stable (empty-dep) fetch callbacks read the
  // current value without a stale closure. Empty string = no search (existing
  // pagination path). Server-side (not client-side) because the list is
  // paginated and content search must reach full message bodies past the
  // 100-char preview.
  const searchQueryRef = useRef<string>('');

  // Server-side org scope. The ref mirrors the selected org so the stable
  // (empty-dep) fetch callbacks read the current value without a stale closure.
  // null = cross-org view (platform-admin god-mode); a value narrows the list.
  const orgScopeRef = useRef<string | null>(initialListSnapshot?.orgScope ?? null);

  // Server-side project scope (二级栏目). Mirrors the selected project so the
  // stable (empty-dep) fetch callbacks read the current value without a stale
  // closure. null = all projects in the current org scope; a value narrows the
  // list to that project. Server-side (not client-side) because the list is
  // paginated — a client filter would miss conversations past the loaded window.
  const projectScopeRef = useRef<string | null>(initialListSnapshot?.projectScope ?? null);

  // Server-side active/archived scope. Mirrors the showArchived toggle so the
  // stable (empty-dep) fetch callbacks read the current value without a stale
  // closure. true = active view (default), false = archived view. Server-side
  // (not client-side) because the list is paginated over a recency-ordered pool
  // where archived convs dominate — a fixed page starves active convs, so a
  // client filter alone leaves only the few actives that reached the window.
  const activeFilterRef = useRef<boolean>(initialListSnapshot?.active ?? true);

  // Debounce refs for conversation list refresh
  const lastRefreshRef = useRef<number>(0);
  const isRefreshingRef = useRef<boolean>(false);
  const pendingForcedListRefreshRef = useRef<boolean>(false);

  // Separate debounce refs for single-conversation refresh — prevents a sidebar
  // refresh from blocking a conversation refresh (and vice versa)
  const lastConvRefreshRef = useRef<number>(0);
  const isConvRefreshingRef = useRef<boolean>(false);
  /** A forced refresh that arrived while one was in flight: re-run once after. */
  const pendingForcedConvRefreshRef = useRef<string | null>(null);

  // Track newest non-temp message ID for cursor-based refresh (avoids stale closure)
  const newestMessageIdRef = useRef<string | undefined>(undefined);

  // Tracks the conversation ID most recently requested via loadConversation.
  // A stale in-flight request that completes after the user switches away checks
  // this ref and discards its result if it no longer matches, preventing
  // cross-session message contamination (stale-fetch race condition).
  const loadingConvIdRef = useRef<string | null>(null);

  // Controller for the in-flight detail request. A new detail request aborts
  // the previous one so rapid clicking cannot stack N competing fetches.
  const detailAbortRef = useRef<AbortController | null>(null);

  // Id of the visible (non-silent) detail fetch currently in flight, or null.
  // Distinct from loadingConvIdRef, which keeps pointing at the last requested
  // conversation after the request settles and so cannot answer "still loading?".
  const detailInFlightIdRef = useRef<string | null>(null);

  // Monotonic sequence for LIST requests. Every full-list fetch bumps it and
  // discards its own result if a newer fetch started meanwhile — without this
  // two concurrent fetches (e.g. the favourite filter clearing while the
  // archived bridge flips is_active) race, and the loser's rows land in state
  // under the winner's filter, emptying the rendered list. loadingConvIdRef
  // above guards the SINGULAR conversation fetch only; this guards the plural
  // ones (bug-patterns #481 recurrence).
  const listReqSeqRef = useRef(0);

  // Keep ref in sync with active conversation's newest non-temp message
  useEffect(() => {
    if (activeConversation && activeConversation.messages.length > 0) {
      // Find the newest message that isn't a temp optimistic message. In a
      // personal relay an expired row may already be swept server-side; using
      // it as the after= cursor would make the server ignore the cursor, so
      // skip it (none usable -> undefined -> plain {limit:20} fetch).
      const relayTtl = activeConversation.is_personal_relay ? activeConversation.relay_ttl_seconds : null;
      const nowMs = Date.now();
      for (let i = activeConversation.messages.length - 1; i >= 0; i--) {
        const m = activeConversation.messages[i];
        if (relayTtl && isRelayMessageExpired(m.created_at, relayTtl, nowMs)) continue;
        if (!m.id.startsWith('temp-')) {
          newestMessageIdRef.current = activeConversation.messages[i].id;
          return;
        }
      }
    }
    newestMessageIdRef.current = undefined;
  }, [activeConversation]);

  // Keep the detail cache fresh. Writing here rather than at each fetch site
  // captures EVERY mutation of the active conversation (load, silent refresh,
  // appended messages, submode/provider patches, older-message pagination), so
  // a cached re-visit restores what the user actually last saw.
  useEffect(() => {
    if (!activeConversation) return;
    const cache = conversationDetailCache;
    // delete-then-set re-inserts at the tail = most-recently-used.
    cache.delete(activeConversation.id);
    cache.set(activeConversation.id, { conversation: activeConversation, hasMore, oldestMessageId });
    while (cache.size > DETAIL_CACHE_MAX_ENTRIES) {
      const lru = cache.keys().next().value;
      if (lru === undefined) break;
      cache.delete(lru);
    }
  }, [activeConversation, hasMore, oldestMessageId]);

  // Keep the tab-lifetime sidebar snapshot in step with the rendered list.
  // Skipped while a full load is in flight (the refs may already hold the NEW
  // scope while the list is still the old one) and while a search is active (a
  // search result is not the sidebar's resting state).
  useEffect(() => {
    if (!listFetchedRef.current || isListLoading || searchQueryRef.current) return;
    conversationListSnapshot = {
      conversations,
      hasMore: hasMoreConversations,
      offset: conversationsOffsetRef.current,
      orgScope: orgScopeRef.current,
      projectScope: projectScopeRef.current,
      favorite: favoriteFilterRef.current,
      active: activeFilterRef.current,
    };
  }, [conversations, hasMoreConversations, isListLoading]);

  /**
   * Silently refresh conversation list without showing loading spinner.
   * Refreshes only the currently loaded range (respects pagination offset).
   * Respects SIDEBAR_REFRESH_DEBOUNCE_MS debounce and prevents concurrent refreshes.
   */
  const silentRefreshConversations = useCallback(async (force = false): Promise<void> => {
    const now = Date.now();
    if (!force && now - lastRefreshRef.current < SIDEBAR_REFRESH_DEBOUNCE_MS) return; // debounce (skippable)
    if (isRefreshingRef.current) {
      if (force) pendingForcedListRefreshRef.current = true;
      return;
    }

    isRefreshingRef.current = true;
    try {
      do {
        pendingForcedListRefreshRef.current = false;
        lastRefreshRef.current = Date.now();
        const seq = ++listReqSeqRef.current;
        try {
          // Refresh only the currently loaded range (offset 0 to current offset).
          // Cap at 199 so that limit (currentCount+1) never exceeds backend le=200.
          const currentCount = Math.min(conversationsOffsetRef.current || CONV_PAGE_SIZE, 199);
          const data = await api.getConversations({ limit: currentCount + 1, offset: 0, favorite: favoriteFilterRef.current || undefined, org_id: orgScopeRef.current || undefined, project_id: projectScopeRef.current || undefined, q: searchQueryRef.current || undefined, is_active: activeFilterRef.current });
          if (seq !== listReqSeqRef.current) continue; // stale response; still drain queued force
          listFetchedRef.current = true;
          if (data && data.length > currentCount) {
            setConversations(data.slice(0, currentCount));
            setHasMoreConversations(true);
          } else {
            setConversations(data || []);
            setHasMoreConversations(false);
          }
        } catch (err) {
          console.error('Silent refresh conversations failed:', err);
        }
      } while (pendingForcedListRefreshRef.current);
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Missed user-level events: attempt reconciliation only while visible.
  // Non-forced refreshes coalesce with recent/in-flight requests in the hook.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void silentRefreshConversations();
    }, SIDEBAR_RECONCILE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [silentRefreshConversations]);

  /**
   * One fetch + merge of the active conversation (the body of
   * silentRefreshConversation; the caller owns the debounce / in-flight guard).
   * Uses after= cursor to fetch only messages newer than the newest known message,
   * then merges by ID (append-only) to preserve scroll position.
   */
  const fetchAndMergeConversation = useCallback(async (conversationId: string): Promise<void> => {
    try {
      // Use ref for cursor to avoid stale closure over activeConversation
      const newestId = newestMessageIdRef.current;

      const detail = await api.getConversation(conversationId,
        newestId ? { limit: 50, after: newestId } : { limit: 20 }
      );
      const freshConv = mapConversationDetail(detail);

      setActiveConversation((prev) => {
        if (!prev || prev.id !== conversationId) {
          // Stale result for a switched-away/deselected conversation — discard
          return prev;
        }
        // Merge: keep existing messages, append only truly new ones.
        // Also replace temp-* optimistic messages matched by content + timestamp proximity.
        const existingIds = new Set(prev.messages.map(m => m.id));
        const newMessages = freshConv.messages.filter(m => !existingIds.has(m.id));
        if (newMessages.length === 0) {
          // pending_action arrives as a freshly deserialized object on every
          // response, so `===` is only meaningful for the both-null case; the
          // structural compare is what actually detects "unchanged". It is
          // key-order sensitive by nature — a reordered payload degrades to
          // today's unconditional re-render (fail-safe), never to a stale UI.
          const paSame =
            prev.pending_action === freshConv.pending_action ||
            JSON.stringify(prev.pending_action) === JSON.stringify(freshConv.pending_action);
          if (
            prev.updated_at === freshConv.updated_at &&
            prev.compact_summary === freshConv.compact_summary &&
            prev.execution_read_only === freshConv.execution_read_only &&
            prev.branched_from_conversation_id === freshConv.branched_from_conversation_id &&
            paSame
          ) {
            return prev;
          }
          // No new messages — update metadata only. pending_action MUST be
          // propagated here: after task_complete the orchestrator may have
          // registered a stage_confirm gate without emitting a new message,
          // and dropping it left the StageAdvanceCard invisible
          // (P-COND-45DA25450F43 Sub-Task 3 CRITICAL fix).
          return {
            ...prev,
            is_group: freshConv.is_group,
            updated_at: freshConv.updated_at,
            compact_summary: freshConv.compact_summary,
            pending_action: freshConv.pending_action,
            execution_read_only: freshConv.execution_read_only,
            branched_from_conversation_id: freshConv.branched_from_conversation_id,
            provider_profile: freshConv.provider_profile,
            architecture_mode: freshConv.architecture_mode,
            backend_kind: freshConv.backend_kind,
          };
        }
        // Find temp messages that match incoming server messages (dedup optimistic updates)
        const tempReplacements = new Set<string>(); // temp IDs to remove
        // Pair newest-with-newest, each temp consumed at most once: reconciled
        // echoes stay temp-* (provisionalEchoId), so an earlier same-text turn
        // may still hold a temp bubble whose row is older than the cursor and
        // must not absorb this turn's row.
        for (let j = newMessages.length - 1; j >= 0; j--) {
          const newMsg = newMessages[j];
          if (newMsg.role !== 'user') continue;
          for (let i = prev.messages.length - 1; i >= 0; i--) {
            const existing = prev.messages[i];
            if (!existing.id.startsWith('temp-')) continue;
            if (existing.role !== 'user') continue;
            if (tempReplacements.has(existing.id)) continue;
            // Match on content alone — newMessages comes from an after=<newestId>
            // cursor fetch (only messages newer than the newest known id), so it
            // cannot carry a historical same-text duplicate. The former 5s
            // timestamp-proximity gate was removed because failed/resent messages
            // land far outside it, leaving the temp failed bubble stuck forever
            // (P-COND-CD75F58BADF9 follow-up).
            if (existing.content === newMsg.content) {
              tempReplacements.add(existing.id);
              break;
            }
          }
        }
        // Remove matched temp messages, then merge and sort
        const cleaned = tempReplacements.size > 0
          ? prev.messages.filter(m => !tempReplacements.has(m.id))
          : prev.messages;
        return {
          ...prev,
          messages: sortByCreatedAt([...cleaned, ...newMessages]),
          is_group: freshConv.is_group,
          updated_at: freshConv.updated_at,
          compact_summary: freshConv.compact_summary,
          // Same propagation as the no-new-messages branch above — keep the
          // server-side pending_action (stage_confirm gate etc.) in sync.
          pending_action: freshConv.pending_action,
          execution_read_only: freshConv.execution_read_only,
          branched_from_conversation_id: freshConv.branched_from_conversation_id,
          // …and the routing triple. This branch (there ARE new messages) is the
          // one a running dispatch always takes, so omitting these left the
          // routing fields frozen at their page-load values for exactly the
          // scenario where they change (mid-run claude↔native switching).
          provider_profile: freshConv.provider_profile,
          architecture_mode: freshConv.architecture_mode,
          backend_kind: freshConv.backend_kind,
        };
      });
    } catch (err) {
      console.error('Silent refresh conversation failed:', err);
    }
  }, []);

  /**
   * Silently refresh active conversation messages without loading spinner.
   * Respects CONV_REFRESH_DEBOUNCE_MS debounce and prevents concurrent refreshes.
   * `opts.force` (task_complete: pending_action was just committed) skips the
   * debounce — mirror of silentRefreshConversations(force) — and, when a refresh
   * is already in flight (it may have read the row BEFORE the commit), is
   * remembered and re-run once that one finishes instead of being dropped.
   */
  const silentRefreshConversation = useCallback(async (
    conversationId: string, opts?: { force?: boolean },
  ): Promise<void> => {
    const force = opts?.force === true;
    if (!force && Date.now() - lastConvRefreshRef.current < CONV_REFRESH_DEBOUNCE_MS) return; // debounce
    if (isConvRefreshingRef.current) {
      if (force) pendingForcedConvRefreshRef.current = conversationId;
      return;
    }
    let target: string | null = conversationId;
    while (target) {
      isConvRefreshingRef.current = true;
      lastConvRefreshRef.current = Date.now();
      try {
        await fetchAndMergeConversation(target);
      } finally {
        isConvRefreshingRef.current = false;
      }
      target = pendingForcedConvRefreshRef.current;
      pendingForcedConvRefreshRef.current = null;
    }
  }, [fetchAndMergeConversation]);

  // silentRefreshConversation cannot carry this field: it is debounced 5s, it
  // early-returns when updated_at is unchanged, and neither merge branch spreads
  // collab_submode. Patch the field directly instead.
  const updateConversationSubmode = useCallback((
    conversationId: string,
    submode: 'planning' | 'design' | 'production' | null,
  ): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      return { ...prev, collab_submode: submode };
    });
  }, []);

  const updateConversationProviderProfile = useCallback((
    conversationId: string,
    profile: string | null,
    routing?: {
      architecture_mode?: Conversation['architecture_mode'];
      backend_kind?: Conversation['backend_kind'];
    },
  ): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      const next = { ...prev, provider_profile: profile };
      // Only overwrite when the caller actually has a server-resolved value —
      // an optimistic pre-flight call passes no routing and must not blank the
      // fields out (undefined would otherwise erase a correct architecture).
      if (routing?.architecture_mode !== undefined) {
        next.architecture_mode = routing.architecture_mode;
      }
      if (routing?.backend_kind !== undefined) {
        next.backend_kind = routing.backend_kind;
      }
      return next;
    });
  }, []);

  const updateConversationArchitectureMode = useCallback((
    conversationId: string,
    architectureMode: Conversation['architecture_mode'],
  ): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      if (prev.architecture_mode === architectureMode) return prev;
      return { ...prev, architecture_mode: architectureMode };
    });
  }, []);

  const updateConversationDevSubmode = useCallback((
    conversationId: string,
    devSubmode: 'branch_dev' | 'operation' | null,
  ): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      return { ...prev, dev_submode: devSubmode };
    });
  }, []);

  /**
   * Loads the first page of conversation summaries from the API.
   * Uses LIMIT+1 pattern: requests PAGE_SIZE+1, if that many return then hasMore=true.
   * Backend returns ConversationSummary[] directly (no wrapper).
   */
  const loadConversations = useCallback(async (): Promise<void> => {
    const seq = ++listReqSeqRef.current;
    listLoadingOwnerRef.current = seq;
    setIsListLoading(true);
    setListError(null);
    try {
      const data = await retryWithBackoff(() =>
        api.getConversations({ limit: CONV_PAGE_SIZE + 1, offset: 0, favorite: favoriteFilterRef.current || undefined, org_id: orgScopeRef.current || undefined, project_id: projectScopeRef.current || undefined, q: searchQueryRef.current || undefined, is_active: activeFilterRef.current })
      );
      if (seq !== listReqSeqRef.current) return; // superseded by a newer list fetch
      listFetchedRef.current = true;
      if (data && data.length > CONV_PAGE_SIZE) {
        setConversations(data.slice(0, CONV_PAGE_SIZE));
        setHasMoreConversations(true);
      } else {
        setConversations(data || []);
        setHasMoreConversations(false);
      }
      conversationsOffsetRef.current = CONV_PAGE_SIZE;
    } catch (err) {
      if (seq !== listReqSeqRef.current) return;
      setListError(getErrorMessage(err, tRef.current));
      console.error('Error loading conversations:', err);
    } finally {
      // A silent refresh may supersede data, but cannot steal spinner cleanup.
      if (seq === listLoadingOwnerRef.current) setIsListLoading(false);
    }
  }, []);

  /**
   * Loads the next page of conversations and appends them to the list.
   * Uses LIMIT+1 pattern for determining if more pages exist.
   */
  const loadMoreConversations = useCallback(async (): Promise<void> => {
    if (isLoadingMoreConversations || !hasMoreConversations) return;
    setIsLoadingMoreConversations(true);
    // Pagination is additive, so it OBSERVES the sequence instead of bumping it:
    // bumping would discard an in-flight full reload and then append this page
    // onto the stale list it was meant to replace.
    const seq = listReqSeqRef.current;
    try {
      const offset = conversationsOffsetRef.current;
      const data = await api.getConversations({ limit: CONV_PAGE_SIZE + 1, offset, favorite: favoriteFilterRef.current || undefined, org_id: orgScopeRef.current || undefined, project_id: projectScopeRef.current || undefined, q: searchQueryRef.current || undefined, is_active: activeFilterRef.current });
      if (seq !== listReqSeqRef.current) return; // a full reload started meanwhile
      if (data && data.length > CONV_PAGE_SIZE) {
        const page = data.slice(0, CONV_PAGE_SIZE);
        setConversations((prev) => {
          // Deduplicate by ID (in case of concurrent refreshes)
          const existingIds = new Set(prev.map((c) => c.id));
          const newConvs = page.filter((c) => !existingIds.has(c.id));
          return [...prev, ...newConvs];
        });
        setHasMoreConversations(true);
      } else {
        setConversations((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newConvs = (data || []).filter((c) => !existingIds.has(c.id));
          return [...prev, ...newConvs];
        });
        setHasMoreConversations(false);
      }
      conversationsOffsetRef.current = offset + CONV_PAGE_SIZE;
    } catch (err) {
      console.error('Failed to load more conversations:', err);
    } finally {
      setIsLoadingMoreConversations(false);
    }
  }, [isLoadingMoreConversations, hasMoreConversations]);

  /**
   * Loads a specific conversation with its most recent messages (paginated).
   * Backend returns ConversationDetail directly (no wrapper).
   *
   * ``silent`` skips the spinner/error visual transitions but keeps every
   * stale-fetch guard — it exists so a cache-hit revalidate does not flash the
   * skeleton over data that is already on screen. It is deliberately NOT
   * silentRefreshConversation: that path is debounced 5s and would swallow the
   * refresh entirely.
   */
  const loadConversation = useCallback(async (
    conversationId: string,
    options?: { silent?: boolean },
  ): Promise<void> => {
    const silent = options?.silent === true;
    // Mark this as the current intended conversation. Any prior in-flight
    // request for a different conversation will see a mismatch on completion
    // and discard its stale result (stale-fetch guard).
    const seq = ++detailReqSeqRef.current;
    loadingConvIdRef.current = conversationId;
    failedDetailIdRef.current = null;
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    // Set unconditionally: the request just aborted above may have been a
    // visible load that will never reach its own cleanup branch (its id no
    // longer matches), and a marker left dangling would block a later retry.
    detailInFlightIdRef.current = silent ? null : conversationId;
    if (!silent) {
      setIsDetailLoading(true);
      setDetailError(null);
    }
    try {
      const detail = await retryWithBackoff(() =>
        api.getConversation(conversationId, { limit: 20 }, { signal: controller.signal })
      );
      // Discard stale result: user switched to another conversation while
      // this request was in-flight.
      if (loadingConvIdRef.current !== conversationId || seq !== detailReqSeqRef.current) return;
      setActiveConversation(mapConversationDetail(detail));
      setHasMore(detail.has_more ?? false);
      setOldestMessageId(detail.oldest_message_id ?? null);
    } catch (err) {
      // An aborted request was superseded on purpose — not a failure.
      if (isCancelledError(err)) return;
      if (loadingConvIdRef.current !== conversationId || seq !== detailReqSeqRef.current) return;
      if (silent) {
        // Cached data is already on screen; a failed revalidate must not
        // replace it with an error state.
        console.warn('Background conversation refresh failed:', err);
        return;
      }
      failedDetailIdRef.current = conversationId;
      setDetailError(getErrorMessage(err, tRef.current));
      console.error('Error loading conversation:', err);
    } finally {
      // Only clear the loading indicator for the conversation we're still on.
      if (!silent && loadingConvIdRef.current === conversationId && seq === detailReqSeqRef.current) {
        detailInFlightIdRef.current = null;
        setIsDetailLoading(false);
      }
    }
  }, []);

  /**
   * Loads older messages for the active conversation (pagination).
   * Prepends older messages to existing ones and updates cursor state.
   */
  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (!activeConversation || !oldestMessageId || isLoadingMore) return;
    // Capture the target BEFORE the await — the user may switch conversations
    // while the fetch is in flight, and the write must not land in the new one.
    const expectedConvId = activeConversation.id;
    setIsLoadingMore(true);
    try {
      const detail = await api.getConversation(expectedConvId, {
        limit: 20,
        before: oldestMessageId,
      });
      const olderMessages = mapConversationDetail(detail).messages;
      // Prepend older messages to existing messages, deduplicating by ID, then sort
      setActiveConversation((prev) => {
        if (!prev || prev.id !== expectedConvId) return prev;
        const existingIds = new Set(prev.messages.map(m => m.id));
        const newMsgs = olderMessages.filter(m => !existingIds.has(m.id));
        return {
          ...prev,
          messages: sortByCreatedAt([...newMsgs, ...prev.messages]),
        };
      });
      // Cursor state belongs to expectedConvId too — writing A's cursor while B
      // is active would corrupt B's pagination. loadingConvIdRef is the
      // synchronously-maintained "intended conversation" marker.
      if (loadingConvIdRef.current !== expectedConvId) return;
      setHasMore(detail.has_more ?? false);
      setOldestMessageId(detail.oldest_message_id ?? null);
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeConversation, oldestMessageId, isLoadingMore]);

  /**
   * Creates a new conversation.
   * Requires project_id (UUID). Returns the ID of the newly created conversation.
   */
  const createConversation = useCallback(
    async (projectId: string, initialMessage?: string): Promise<string> => {
      setCreateError(null);
      try {
        const detail = await api.createConversation({
          project_id: projectId,
          initial_message: initialMessage,
        });
        return detail.id;
      } catch (err) {
        setCreateError('Failed to create conversation');
        throw err;
      }
    },
    []
  );

  /**
   * Adds a new message to the active conversation (optimistic update).
   */
  const addMessage = useCallback((message: MessageData, expectedConvId: string): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== expectedConvId) return prev;
      // Dedup: skip if message with same ID already exists
      if (prev.messages.some(m => m.id === message.id)) return prev;
      return {
        ...prev,
        messages: sortByCreatedAt([...prev.messages, message]),
        updated_at: nowCSTISO(),
      };
    });
  }, []);

  /**
   * Updates an existing message content (used during streaming).
   */
  const updateMessage = useCallback((messageId: string, content: string, expectedConvId: string, status?: MessageData['status']): void => {
    setActiveConversation((prev) => {
      if (!prev || prev.id !== expectedConvId) return prev;
      return {
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === messageId
            ? { ...msg, content, ...(status !== undefined ? { status } : {}) }
            : msg
        ),
      };
    });
  }, []);

  /**
   * Reconciles an own-session user echo from the server (real id, no status)
   * with the optimistic temp-* bubble inserted at send time. Finds the matching
   * temp user bubble (content equal, status 'sending'|'failed') and replaces it
   * in place with the server echo (temp id retained, see provisionalEchoId) — clearing the failed/sending
   * indicator and eliminating the duplicate row. Falls back to appending when no
   * matching optimistic bubble exists (preserves cross-tab/device echo display).
   * This is the event-driven reconciliation path that does not depend on the
   * timestamp-proximity window in silentRefreshConversation (P-COND-CD75F58BADF9
   * follow-up: failed/resent messages land far outside that 5s window).
   */
  const reconcileOptimisticUserMessage = useCallback((
    serverMsg: MessageData,
    expectedConvId: string,
    opts?: { adoptServerId?: boolean },
  ): void => {
    // Only a `persisted_row: true` frame (personal relay) carries the real DB
    // id; adopting it lets ⋯ 删除 target the row. Everything else keeps the
    // provisional temp id exactly as before (O14-2, provisionalEchoId).
    const adopt = opts?.adoptServerId === true;
    setActiveConversation((prev) => {
      if (!prev || prev.id !== expectedConvId) return prev;
      if (adopt) {
        if (prev.messages.some((m) => m.id === serverMsg.id)) return prev;
        const tIdx = prev.messages.findIndex(
          (m) =>
            m.id.startsWith('temp-') &&
            m.role === 'user' &&
            m.content === serverMsg.content &&
            (m.status === 'sending' || m.status === 'failed')
        );
        const next = tIdx === -1 ? [...prev.messages, serverMsg] : [...prev.messages];
        if (tIdx !== -1) next[tIdx] = serverMsg;
        return { ...prev, messages: sortByCreatedAt(next), updated_at: nowCSTISO() };
      }
      // Already reconciled (same echo delivered twice) — no-op.
      const echoId = provisionalEchoId(serverMsg.id);
      if (prev.messages.some((m) => m.id === serverMsg.id || m.id === echoId)) return prev;
      const idx = prev.messages.findIndex(
        (m) =>
          m.id.startsWith('temp-') &&
          m.role === 'user' &&
          m.content === serverMsg.content &&
          (m.status === 'sending' || m.status === 'failed')
      );
      if (idx === -1) {
        // No optimistic bubble to replace — fall back to append (existing behavior),
        // under a provisional id (the echo id is not the DB id, see provisionalEchoId).
        return {
          ...prev,
          messages: sortByCreatedAt([...prev.messages, { ...serverMsg, id: echoId }]),
          updated_at: nowCSTISO(),
        };
      }
      const replaced = [...prev.messages];
      // Take the echo's content/metadata and drop the sending/failed status, but
      // keep the temp id: the echo id is not the DB id (see provisionalEchoId).
      replaced[idx] = { ...serverMsg, id: prev.messages[idx].id };
      return {
        ...prev,
        messages: sortByCreatedAt(replaced),
        updated_at: nowCSTISO(),
      };
    });
  }, []);

  /**
   * Sets the active conversation by ID.
   *
   * Three paths: re-selecting the conversation already on screen is a no-op;
   * a cached conversation renders instantly and revalidates in the background;
   * an uncached one clears first and loads with the spinner.
   */
  const setActiveConversationId = useCallback(
    (conversationId: string | null): void => {
      if (conversationId) {
        // Already rendered — re-clicking the open conversation must not clear
        // the screen nor issue another request.
        if (activeConversationRef.current?.id === conversationId) return;

        // Already fetching exactly this conversation. Restarting would abort a
        // request that is nearly done, so an impatient user clicking through a
        // slow load would push the finish line away with every click.
        if (detailInFlightIdRef.current === conversationId) return;

        const cache = conversationDetailCache;
        const cached = cache.get(conversationId);
        if (cached) {
          // delete-then-set re-inserts at the tail = most-recently-used.
          cache.delete(conversationId);
          cache.set(conversationId, cached);
          setActiveConversation(cached.conversation);
          setHasMore(cached.hasMore);
          setOldestMessageId(cached.oldestMessageId);
          setDetailError(null);
          // A superseded non-silent load never reaches its own cleanup branch
          // (its id no longer matches), so clear the spinner here explicitly.
          setIsDetailLoading(false);
          failedDetailIdRef.current = null;
          void loadConversation(conversationId, { silent: true });
          return;
        }

        // Uncached: clear immediately so stale data from the previous
        // conversation is never displayed during the loading window
        // (bug-patterns #481 Occ.3).
        setActiveConversation(null);
        void loadConversation(conversationId);
        return;
      }

      setActiveConversation(null);
      // Deselect must also cancel in-flight writes, otherwise a pending
      // loadMoreMessages still matches the previous conversation's marker.
      detailAbortRef.current?.abort();
      detailAbortRef.current = null;
      detailInFlightIdRef.current = null;
      loadingConvIdRef.current = null;
      detailReqSeqRef.current += 1;
      failedDetailIdRef.current = null;
      setIsDetailLoading(false);
      setDetailError(null);
    },
    [loadConversation]
  );

  /**
   * Optimistically removes a conversation from the local list.
   * Used for instant UI feedback before the API confirms the action.
   */
  /**
   * Removes messages by id (personal relay: ⋯ 删除, 清空, `messages_deleted`
   * frames). A non-active conversation only has its cached snapshot dropped so
   * the next visit refetches instead of showing deleted rows.
   */
  const removeMessages = useCallback((ids: string[], convId: string): void => {
    if (ids.length === 0) return;
    const drop = new Set(ids);
    if (activeConversationRef.current?.id !== convId) {
      conversationDetailCache.delete(convId);
    }
    setActiveConversation((prev) => {
      if (!prev || prev.id !== convId) return prev;
      if (!prev.messages.some((m) => drop.has(m.id))) return prev;
      return { ...prev, messages: prev.messages.filter((m) => !drop.has(m.id)) };
    });
  }, []);

  const removeConversation = useCallback((conversationId: string): void => {
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
  }, []);

  /**
   * Evicts a conversation from the detail cache. The active conversation's
   * entry is rewritten by the cache-sync effect on its next change, so callers
   * only need this for conversations that changed off-screen.
   */
  const invalidateConversationCache = useCallback((id: string): void => {
    conversationDetailCache.delete(id);
  }, []);

  /**
   * Optimistically marks a conversation as read in the local list.
   * Also calls the backend API to persist the read status.
   */
  const markConversationRead = useCallback((conversationId: string): void => {
    setConversations((prev) =>
      prev.map((c) => c.id === conversationId ? { ...c, has_unread: false } : c)
    );
    api.markConversationRead(conversationId).catch(() => {});
  }, []);

  /**
   * Sets the server-side "favorites only" filter. Updates the state + ref,
   * then reloads the first page so the favorite=true query runs (favorited
   * convs past the pagination window surface). Mirrors the date filter's
   * ownership but is server-side because the list is paginated.
   */
  const setFavoriteFilter = useCallback((next: boolean): void => {
    favoriteFilterRef.current = next;
    setFavoriteFilterState(next);
    conversationsOffsetRef.current = 0;
    // loadConversations reads favoriteFilterRef.current (always fresh).
    loadConversations();
  }, [loadConversations]);

  /**
   * Sets the server-side search query and reloads the first page so the
   * server-side q filter runs (会话ID/分支/标题/内容). Mirrors setFavoriteFilter's
   * ownership but is server-side because the list is paginated and content
   * search must reach full message bodies past the 100-char preview. The caller
   * (ConversationList) already debounces, so this fires once per settled term.
   */
  const setSearchFilter = useCallback((next: string): void => {
    searchQueryRef.current = next;
    conversationsOffsetRef.current = 0;
    // loadConversations reads searchQueryRef.current (always fresh).
    loadConversations();
  }, [loadConversations]);

  /**
   * Sets the org scope for the conversation list and reloads the first page so
   * the server-side org_id filter runs. Mirrors setFavoriteFilter's ownership
   * but is server-side because the list is paginated (a client-side filter
   * would miss conversations beyond the loaded window). null = cross-org view.
   */
  const setOrgScope = useCallback((orgId: string | null): void => {
    if (orgScopeRef.current === orgId) return;
    orgScopeRef.current = orgId;
    conversationsOffsetRef.current = 0;
    // loadConversations reads orgScopeRef.current (always fresh).
    loadConversations();
  }, [loadConversations]);

  /**
   * Sets the project scope (二级栏目) for the conversation list and reloads the
   * first page so the server-side project_id filter runs. Mirrors setOrgScope —
   * server-side (not client-side) because the list is paginated, so a client
   * filter would miss conversations beyond the loaded window. null = all
   * projects in the current org scope.
   */
  const setProjectScope = useCallback((projectId: string | null): void => {
    if (projectScopeRef.current === projectId) return;
    projectScopeRef.current = projectId;
    conversationsOffsetRef.current = 0;
    // loadConversations reads projectScopeRef.current (always fresh).
    loadConversations();
  }, [loadConversations]);

  /**
   * Sets the active/archived scope for the conversation list and reloads the
   * first page so the server-side is_active filter runs. Mirrors setProjectScope
   * — server-side (not client-side) because the list is paginated over a
   * recency-ordered pool where archived convs dominate, so a client filter alone
   * starves the active view. true = active view (default), false = archived.
   */
  const setActiveFilter = useCallback((next: boolean): void => {
    if (activeFilterRef.current === next) return;
    activeFilterRef.current = next;
    conversationsOffsetRef.current = 0;
    // loadConversations reads activeFilterRef.current (always fresh).
    loadConversations();
  }, [loadConversations]);

  /**
   * Optimistically flips a conversation's favorite (heart) state in the local
   * list, then persists via the API. Reverts on failure. If the favorites-only
   * filter is active and the conv is being un-favorited, it is removed from the
   * list (it no longer matches the server-side filter).
   */
  const toggleConversationFavorite = useCallback((conversationId: string, next: boolean): void => {
    setConversations((prev) => {
      if (favoriteFilterRef.current && !next) {
        // Un-favoriting while the favorites filter is on — drop it from view.
        return prev.filter((c) => c.id !== conversationId);
      }
      return prev.map((c) => c.id === conversationId ? { ...c, is_favorite: next } : c);
    });
    api.toggleFavorite(conversationId, next).catch((err) => {
      console.error('Toggle favorite failed:', err);
      // Revert: restore the prior state by reloading the current view.
      silentRefreshConversations(true);
    });
  }, [silentRefreshConversations]);

  /** Dismiss only the selected resource's error. */
  const clearError = useCallback((resource: 'list' | 'detail' | 'create'): void => {
    if (resource === 'list') setListError(null);
    else if (resource === 'create') setCreateError(null);
    else {
      setDetailError(null);
      failedDetailIdRef.current = null;
    }
  }, []);

  /** Retry the failed resource, even if its detail never became active. */
  const retryLoad = useCallback((resource: 'list' | 'detail'): void => {
    if (resource === 'list') void loadConversations();
    else if (failedDetailIdRef.current) void loadConversation(failedDetailIdRef.current);
  }, [loadConversation, loadConversations]);

  return {
    conversations,
    activeConversation,
    isListLoading,
    isDetailLoading,
    listError,
    detailError,
    createError,
    hasMore,
    isLoadingMore,
    hasMoreConversations,
    isLoadingMoreConversations,
    loadConversations,
    loadConversation,
    loadMoreMessages,
    loadMoreConversations,
    createConversation,
    addMessage,
    updateMessage,
    reconcileOptimisticUserMessage,
    removeMessages,
    setActiveConversationId,
    silentRefreshConversations,
    hasWarmConversationList: initialListSnapshot !== null,
    silentRefreshConversation,
    updateConversationSubmode,
    updateConversationProviderProfile,
    updateConversationArchitectureMode,
    updateConversationDevSubmode,
    removeConversation,
    markConversationRead,
    invalidateConversationCache,
    favoriteFilter,
    setFavoriteFilter,
    setSearchFilter,
    setOrgScope,
    setProjectScope,
    setActiveFilter,
    toggleConversationFavorite,
    clearError,
    retryLoad,
  };
};

export default useConversation;
